import React, { ReactNode, useCallback } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { SharedValue, useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { CloseIcon } from '@/assets/icons/close';
import { TeamIcon } from '@/components/EventCard';
import { GameTrackerButton } from '@/components/GameTrackerButton';
import { Text } from '@/components/TextComponent';
import { HudTeam } from '@/components/event-hud/EventHudDetails';
import { getTeamColors } from '@/components/event-hud/utils';
import { Box, Row, TouchableBox } from '@/components/lib/components';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { Event } from '@/feature/event-details-sbk/types';
import { useGameTrackerConfig } from '@/hooks/use-game-tracker-config';
import { RootStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { isWeb } from '@/utils/constants-platform-specific';

import { ds } from '../lib/design.system';

export const EVENT_SHEET_TITLE_HEIGHT = 48;

const ICON_SIZE = 20;

export const TRANSLATE_DISTANCE = 24;

const leagueToScoreWidth: { [key in League]?: number } = {
    [League.Nfl]: 23,
    [League.NflFullSeason]: 23,
    [League.Nhl]: 23,
    [League.Cfb]: 23,
    [League.Mlb]: 23,
    [League.Nba]: 31,
    [League.Euroleague]: 31,
    [League.Wnba]: 31,
    [League.Wcbb]: 31,
    [League.Cbb]: 31,
};

type EventSheetTitleProps = {
    hasTeams: boolean;
    eventTitle: ReactNode | string;
    eventSubTitle?: string;
    showGameTracker: boolean;
    pamEvent?: Event;
    scrollOffset: SharedValue<number>;
    hudHeight: number;
    eventDetails: {
        matchDetails?: string;
        league: League;
        eventTime: string;
        isLive?: boolean;
    };
    teamDetails: {
        homeScore?: number;
        awayScore?: number;
        homeTeam?: HudTeam;
        awayTeam?: HudTeam;
    };
};

export const EventSheetTitle = ({
    hasTeams,
    eventTitle,
    eventSubTitle,
    showGameTracker,
    pamEvent,
    scrollOffset,
    hudHeight,
    teamDetails,
    eventDetails,
}: EventSheetTitleProps) => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList, 'FantasyEventsSheets'>>();
    const { bottomSheetCircularPagerRef } = useFullScreenPagerTransition();

    // on web we navigate back to the previous screen
    // on mobile we close the bottom sheet
    const onClose = useCallback(() => {
        if (isWeb) {
            navigation.goBack();
            return;
        }

        bottomSheetCircularPagerRef.current?.close();
    }, [bottomSheetCircularPagerRef, navigation]);

    const isScrolled = useDerivedValue(() => {
        return scrollOffset.value > hudHeight;
    });

    const eventTitleStyle = useAnimatedStyle(() => {
        if (!hasTeams) {
            return {};
        }
        return {
            opacity: withTiming(isScrolled.value ? 0 : 1, { duration: 200 }),
            transform: [
                {
                    translateY: withTiming(isScrolled.value ? -TRANSLATE_DISTANCE : 0, { duration: 200 }),
                },
            ],
        };
    });

    const eventDetailsStyle = useAnimatedStyle(() => {
        if (!hasTeams) {
            return {};
        }
        return {
            opacity: withTiming(isScrolled.value ? 1 : 0, { duration: 200 }),
            transform: [
                {
                    translateY: withTiming(isScrolled.value ? 0 : TRANSLATE_DISTANCE, { duration: 200 }),
                },
            ],
        };
    });

    const { homeScore, awayScore, homeTeam, awayTeam } = teamDetails;
    const { matchDetails, league, eventTime, isLive } = eventDetails;

    return (
        <Row
            height={EVENT_SHEET_TITLE_HEIGHT}
            paddingHorizontal={'s16'}
            paddingVertical={'s12'}
            justifyContent={'space-between'}
            alignItems={'center'}
        >
            <TouchableBox
                onPress={onClose}
                hitSlop={12}
                width={ICON_SIZE}
                height={ICON_SIZE}
                alignItems={'center'}
                justifyContent={'center'}
                testID={'event-sheet-close-button'}
            >
                <CloseIcon width={ICON_SIZE} height={ICON_SIZE} />
            </TouchableBox>
            <Animated.View
                style={[
                    common.zIndexNegative1,
                    StyleSheet.absoluteFill,
                    common.justifyCenter,
                    common.alignCenter,
                    eventTitleStyle,
                ]}
            >
                {typeof eventTitle === 'string' ? (
                    <Text
                        variant={'titleLarge'}
                        numberOfLines={eventSubTitle ? 1 : 2}
                        textAlign={'center'}
                        style={styles.titlePadding}
                    >
                        {eventTitle}
                    </Text>
                ) : (
                    eventTitle
                )}
                {eventSubTitle ? (
                    <Text variant={'bodySmall'} color={'neutral300'} numberOfLines={1} style={styles.titlePadding}>
                        {eventSubTitle}
                    </Text>
                ) : null}
            </Animated.View>
            <Box width={ICON_SIZE} height={ICON_SIZE} alignItems={'center'} justifyContent={'center'}>
                {showGameTracker && pamEvent ? <EventGameTracker pamEvent={pamEvent} /> : null}
            </Box>
            {hasTeams ? (
                <Animated.View
                    style={[
                        common.zIndexNegative1,
                        StyleSheet.absoluteFill,
                        common.justifyCenter,
                        common.alignCenter,
                        eventDetailsStyle,
                    ]}
                >
                    <TeamEventDetailedInfoRow
                        awayTeam={awayTeam}
                        homeTeam={homeTeam}
                        league={league}
                        eventTime={eventTime}
                        awayScore={awayScore}
                        homeScore={homeScore}
                        isLive={isLive}
                        matchDetails={matchDetails}
                    />
                </Animated.View>
            ) : null}
        </Row>
    );
};

type TeamEventDetailedInfoRowProps = {
    homeScore?: number;
    awayScore?: number;
    homeTeam?: HudTeam;
    awayTeam?: HudTeam;
    matchDetails?: string;
    league: League;
    eventTime: string;
    isLive?: boolean;
};

export const TeamEventDetailedInfoRow = ({
    homeScore,
    awayScore,
    homeTeam,
    awayTeam,
    matchDetails,
    league,
    eventTime,
    isLive,
}: TeamEventDetailedInfoRowProps) => {
    const { home, away } = getTeamColors(
        awayTeam && homeTeam
            ? {
                  awayTeam,
                  homeTeam,
              }
            : undefined
    );

    return (
        <Row gap={'s12'} alignItems={'center'}>
            <TeamIcon
                width={28}
                height={28}
                icon={awayTeam?.logoUrl || ''}
                league={league}
                primaryColor={away.primary}
                secondaryColor={away.secondary}
            />
            {isLive ? (
                <>
                    <Box width={leagueToScoreWidth[league] || 23} alignItems={'center'}>
                        <Text variant={'titleLarge'}>{awayScore}</Text>
                    </Box>
                    <Box minHeight={32} alignItems={'center'} justifyContent={'center'}>
                        <Text
                            variant={'labelMedium'}
                            color={ds.colorToken.text.danger}
                            numberOfLines={2}
                            textAlign={'center'}
                        >
                            {matchDetails || 'LIVE'}
                        </Text>
                    </Box>
                    <Box width={leagueToScoreWidth[league] || 23} alignItems={'center'}>
                        <Text variant={'titleLarge'}>{homeScore}</Text>
                    </Box>
                </>
            ) : (
                <Text variant={'titleLarge'}>{eventTime}</Text>
            )}
            <TeamIcon
                width={28}
                height={28}
                icon={homeTeam?.logoUrl || ''}
                league={league}
                primaryColor={home.primary}
                secondaryColor={home.secondary}
            />
        </Row>
    );
};

const styles = StyleSheet.create({
    titlePadding: { paddingHorizontal: 52 },
});

export const EventGameTracker = ({ pamEvent }: { pamEvent: Event }) => {
    const gameTrackerConfig = useGameTrackerConfig(pamEvent.league, pamEvent.event_details);

    return gameTrackerConfig ? (
        <GameTrackerButton config={gameTrackerConfig} iconColor={ds.colorValue.icon.primary} iconSize={ICON_SIZE} />
    ) : null;
};
